import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { AlertCircle, Users } from "lucide-react";

export const DuplicateFileModal = ({ duplicateModal, setDuplicateModal, handleDuplicateAction }) => (
  <Dialog open={duplicateModal.open} onOpenChange={(open) => !open && setDuplicateModal({ ...duplicateModal, open: false })}>
    <DialogContent data-testid="duplicate-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          Duplicate File Detected
        </DialogTitle>
        <DialogDescription>
          The file <strong className="text-slate-900">{duplicateModal.filename}</strong> has already been imported 
          with <strong className="text-slate-900">{duplicateModal.existingCount}</strong> records.
        </DialogDescription>
      </DialogHeader>
      <div className="py-4">
        <p className="text-sm text-slate-600 mb-4">What would you like to do?</p>
        <RadioGroup defaultValue="replace" className="space-y-3">
          <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg border">
            <RadioGroupItem value="replace" id="replace" />
            <div>
              <Label htmlFor="replace" className="font-medium">Replace existing data</Label>
              <p className="text-sm text-slate-500">Delete old records and import new ones</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg border">
            <RadioGroupItem value="append" id="append" />
            <div>
              <Label htmlFor="append" className="font-medium">Add to existing data</Label>
              <p className="text-sm text-slate-500">Keep old records and add new ones (may create duplicates)</p>
            </div>
          </div>
        </RadioGroup>
      </div>
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={() => handleDuplicateAction('cancel')} data-testid="duplicate-cancel-btn">Cancel</Button>
        <Button variant="destructive" onClick={() => handleDuplicateAction('replace')} data-testid="duplicate-replace-btn">Replace</Button>
        <Button onClick={() => handleDuplicateAction('append')} data-testid="duplicate-append-btn">Add</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ImportPartySearchModal = ({ partySearchModal, setPartySearchModal, existingParties, assignPartyToTransaction }) => (
  <Dialog open={partySearchModal.open} onOpenChange={(open) => !open && setPartySearchModal({ open: false, txnIndex: null, searchQuery: '' })}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          Search & Assign Party
        </DialogTitle>
        <DialogDescription>
          Search for an existing party to assign to this transaction. This will update the party name and PAN.
        </DialogDescription>
      </DialogHeader>
      <div className="py-2">
        <Input
          placeholder="Type party name to search..."
          value={partySearchModal.searchQuery}
          onChange={(e) => setPartySearchModal(prev => ({ ...prev, searchQuery: e.target.value }))}
          className="mb-3"
          autoFocus
        />
        <div className="max-h-64 overflow-y-auto border rounded-lg">
          {existingParties
            .filter(p => 
              partySearchModal.searchQuery.length < 2 ||
              p.name?.toLowerCase().includes(partySearchModal.searchQuery.toLowerCase()) ||
              p.pan?.toLowerCase().includes(partySearchModal.searchQuery.toLowerCase())
            )
            .slice(0, 50)
            .map((party) => (
              <div
                key={`${party.name}-${party.pan}-${party.gstin}`}
                className="p-3 border-b hover:bg-purple-50 cursor-pointer flex justify-between items-center"
                onClick={() => assignPartyToTransaction(partySearchModal.txnIndex, party)}
              >
                <div>
                  <div className="font-medium">{party.name}</div>
                  <div className="text-xs text-slate-500">
                    PAN: {party.pan || 'N/A'} | GSTIN: {party.gstin || 'N/A'}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-7">Select</Button>
              </div>
            ))}
          {existingParties.filter(p => 
            partySearchModal.searchQuery.length < 2 ||
            p.name?.toLowerCase().includes(partySearchModal.searchQuery.toLowerCase())
          ).length === 0 && (
            <div className="p-6 text-center text-slate-500">
              No parties found matching "{partySearchModal.searchQuery}"
            </div>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Showing up to 50 results. Type more characters to narrow down.
        </p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => setPartySearchModal({ open: false, txnIndex: null, searchQuery: '' })}>
          Cancel
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
